import { Link } from "react-router";
import { format, isToday } from "date-fns";
import { cn } from "@/lib/cn";
import { WEEK_ABBR, activeApptsOnDay, type WeekAppointment } from "@/lib/dashboard-week";
import { AppointmentPill } from "./AppointmentPill";
import { ContagemDoDia } from "./ContagemDoDia";

const MAX_PER_DAY = 3;

/**
 * Visão "Semana" — uma linha só de dias, com a MESMA montagem do calendário do mês.
 *
 * Antes cada célula empacotava a abreviação do dia e o número lado a lado. Em 360px a célula fica
 * com ~40px de largura e ~28px úteis depois do padding; "SEG" mais o círculo de 24px pedem uns
 * 54px, então letras e números estouravam a caixa. O mês nunca teve esse problema porque as
 * abreviações moram numa linha de cabeçalho própria — é esse arranjo que a semana passa a usar.
 *
 * Sem "Mostrar tudo" ligado, cada dia mostra só a contagem: serve como leitura de quanto tem em
 * cada dia. Ligando, abre as consultas dia a dia.
 */
export function WeekDayStrip({
  days,
  appts,
  showAll = false,
  onSelectAppt,
  readOnly = false,
}: {
  days: Date[];
  appts: WeekAppointment[];
  showAll?: boolean;
  onSelectAppt: (appt: WeekAppointment) => void;
  readOnly?: boolean;
}) {
  return (
    <div className="w-full">
      {/* Cabeçalho tirado dos próprios dias, e não de uma lista fixa: assim funciona começando
          a semana no domingo ou na segunda, sem depender da ordem de WEEK_ABBR. */}
      <div className="grid grid-cols-7 gap-1">
        {days.map((d) => (
          <div
            key={`cab-${d.toISOString()}`}
            className="pb-1 text-center text-xs font-semibold uppercase tracking-wide text-zinc-400 dark:text-muted-foreground"
          >
            {WEEK_ABBR[d.getDay()]}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1">
        {days.map((d) => {
          const dayAppts = activeApptsOnDay(appts, d);
          const today = isToday(d);

          const numeroDoDia = (
            <span
              className={cn(
                "flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-sm",
                today ? "bg-brand-600 font-bold text-white" : "text-zinc-700 dark:text-foreground/80",
              )}
            >
              {format(d, "d")}
            </span>
          );

          const casca = cn(
            "flex min-h-24 w-full flex-col rounded-lg border border-zinc-100 p-1.5 transition-colors dark:border-border/50",
            today && "border-brand-200 bg-brand-50/40 dark:border-primary/30 dark:bg-primary/10",
          );

          if (!showAll) {
            const conteudo = (
              <div className={casca}>
                <div className="flex justify-end">{numeroDoDia}</div>
                <div className="mt-1 flex flex-1 flex-col items-start gap-1">
                  <ContagemDoDia total={dayAppts.length} singular="consulta" plural="consultas" />
                </div>
              </div>
            );
            return !readOnly && dayAppts.length > 0 ? (
              <Link key={d.toISOString()} to="/agenda">
                {conteudo}
              </Link>
            ) : (
              <div key={d.toISOString()}>{conteudo}</div>
            );
          }

          return (
            <div key={d.toISOString()} className={casca}>
              <div className="flex justify-end">{numeroDoDia}</div>
              <div className="mt-1 flex w-full flex-1 flex-col gap-1">
                {dayAppts.slice(0, MAX_PER_DAY).map((a) => (
                  <AppointmentPill key={a.id} appt={a} onSelect={onSelectAppt} />
                ))}
                {dayAppts.length > MAX_PER_DAY &&
                  (readOnly ? (
                    <p className="text-center text-[10px] font-medium text-zinc-400 dark:text-muted-foreground">
                      +{dayAppts.length - MAX_PER_DAY}
                    </p>
                  ) : (
                    <Link
                      to="/agenda"
                      className="text-center text-[10px] font-medium text-brand-700 hover:underline dark:text-primary"
                    >
                      +{dayAppts.length - MAX_PER_DAY}
                    </Link>
                  ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
